export const NOISE = `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0);const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.0-g;vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857;vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy;vec4 y=y_*ns.x+ns.yyyy;vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0;vec4 s1=floor(b1)*2.0+1.0;vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}`;

export const surfaceVert = `
varying vec3 vPos;
void main(){
  vec4 w = modelMatrix * vec4(position, 1.0);
  vPos = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

export const surfaceFrag = `
uniform sampler2D uMap;
uniform float uTime, uSpread, uFloorReveal, uAspect, uTileH, uRadius, uCy, uFloor;
uniform vec3 uBase, uFrame;
uniform vec4 uCrop;
varying vec3 vPos;
${NOISE}
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return textureGrad(uMap, mix(uCrop.xy, uCrop.zw, m), dFdx(uv) * 0.96, dFdy(uv) * 0.96).rgb;
}
vec3 flowSample(vec2 uv){
  vec2 f = vec2(sin(uv.y * 5.0 + uTime * 0.21 + sin(uv.x * 3.7)), cos(uv.x * 4.3 - uTime * 0.17 + sin(uv.y * 2.9)));
  float p0 = fract(uTime * 0.07);
  float p1 = fract(uTime * 0.07 + 0.5);
  float w0 = 1.0 - abs(1.0 - 2.0 * p0);
  vec3 a = tap(uv + f * (p0 - 0.5) * 0.03);
  vec3 b = tap(uv + f * (p1 - 0.5) * 0.03);
  return mix(b, a, w0);
}
void main(){
  float k = mix(0.55, 1.0, uSpread);
  vec2 tile = vec2(uTileH * uAspect, uTileH) * k;
  vec2 local = uFloor > 0.5
    ? vec2(vPos.x, -tile.y * 0.5 - (uRadius + vPos.z))
    : vec2(atan(vPos.x, -vPos.z) * uRadius, vPos.y - uCy);
  vec3 paint = flowSample(local / tile + 0.5);
  float n = snoise(vec3(local * 0.35, uTime * 0.05)) * 0.5 + 0.5;
  float m;
  float frame = 0.0;
  if (uFloor > 0.5) {
    float r = 1.0 - length(vPos.xz) / uRadius;
    m = smoothstep(0.0, 0.1, uFloorReveal - r - n * 0.15);
  } else {
    vec2 q = abs(local) / (tile * 0.5);
    float d = max(q.x, q.y);
    // spread until the farthest point of the wall (behind the visitor, up to the top) is covered, for any aspect ratio
    float dMax = max(3.14159 * uRadius / (0.5 * tile.x), (10.0 - uCy) / (0.5 * tile.y)) + 2.2;
    float rr = mix(1.0, max(14.0, dMax), uSpread * uSpread);
    m = 1.0 - smoothstep(rr - 0.4 * uSpread - 0.003, rr, d + n * 1.4 * uSpread);
    frame = (1.0 - smoothstep(0.0, 0.2, uSpread)) * step(1.0, d) * (1.0 - step(1.05, d));
    m *= 1.0 - smoothstep(7.5, 10.0, vPos.y) * 0.85;
  }
  // a dark room around her painting: the paint keeps its true colour (no haze, no white wash, no saturation boost)
  vec3 base = uBase * (0.6 + 0.4 * n);
  vec3 col = mix(base, paint, m);
  col += m * (1.0 - m) * 0.25 * paint;
  col = mix(col, uFrame, frame);
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}`;

export const particleVert = `
attribute vec3 aColor;
uniform float uTime, uSpread, uPix;
varying vec3 vC;
varying float vA;
varying float vAng;
void main(){
  vec3 p = position;
  vAng = fract(position.x * 3.7 + position.z * 1.3) * 6.2832;
  p.y = mod(position.y + uTime * (0.1 + fract(position.x * 7.13) * 0.18), 9.0);
  p.x += sin(uTime * 0.3 + position.z) * 0.35;
  p.z += cos(uTime * 0.25 + position.x) * 0.35;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = uPix * (22.0 + fract(position.z * 3.1) * 34.0) / -mv.z;
  vC = aColor;
  vA = uSpread * smoothstep(0.0, 1.0, p.y) * (1.0 - smoothstep(7.0, 9.0, p.y));
}`;

export const particleFrag = `
varying vec3 vC;
varying float vA;
varying float vAng;
void main(){
  vec2 q = gl_PointCoord - 0.5;
  q = mat2(cos(vAng), -sin(vAng), sin(vAng), cos(vAng)) * q;
  float bend = q.y + q.x * q.x * 0.9;
  float d = length(vec2(q.x, bend * 5.0));
  float bristle = 0.6 + 0.4 * sin(bend * 140.0 + q.x * 9.0);
  float a = smoothstep(0.5, 0.15, d) * bristle * smoothstep(0.5, 0.25, abs(q.x));
  if (a < 0.02) discard;
  gl_FragColor = vec4(vC, a * vA * 0.8);
  #include <colorspace_fragment>
}`;
